/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    useContext, useEffect, useState,
} from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import {
    object, bool, array, element, func,
} from 'prop-types';
import Cookies from 'universal-cookie';

import {
    getPaymentMethod,
    getPaymentMethods,
} from '../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import {
    getPassportSubscriptionStatus,
    getOrderContainsSubscriptionItem,
    isHDFamilyBrandProductInCart,
    getOrderContainsClubItemPayByCreditCard,
    getAmount,
    getAppliedPromotions,
} from '../../../../../../../state/ducks/MfeCheckout/ducks/Cart/Cart-Selectors';
import { isVenmoMethodSupported } from '../../../../../../../state/ducks/Checkout/ducks/Payment/plugins/Venmo/Venmo-Helper';
import { getFeatureFlags } from '../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';
import { getIsGiftCardCoverFullOrderTotal } from '../../../../../../../state/ducks/MfeCheckout/ducks/Payment/GiftCard/GiftCard-Selectors';
import { recordPaymentMethodType } from '../../../../../../../state/ducks/MfeCheckout/ducks/Payment/Payment-Actions';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { setPaymentMethod } from '../../../../../../../state/ducks/Checkout/ducks/Payment/ducks/PaymentForm/PaymentForm-Actions';
import { getIsBannerCodeTried } from '../../../../../../../state/ducks/MfeCheckout/ducks/Attribution/Attribution-Selectors';
import { attributionApplyPromoLinkedPaymentMethod } from '../../../../../../../state/ducks/MfeCheckout/ducks/Attribution/Attribution-Actions';
import { getPaymentPageReady } from '../../../../../../../state/ducks/MfeCheckout/ducks/Payment/PaymentReadiness/PaymentReadiness-Selector';
import { isExternalCartProductInCart } from '../../../../../../../state/ducks/MfeCheckout/ducks/ExternalCart/ExternalCart-Selectors';

const PaymentMethodContext = React.createContext({
    paymentMethodReady: false,
    paymentMethodsSupported: [],
});

export const useGetPaymentMethodsSupported = () => {
    const context = useContext(PaymentMethodContext);

    return context;
};

const listPaymentMethodFlags = {
    GooglePay: 'is-google-pay-enabled',
    Venmo: 'is-venmo-enabled',
    PayPal: 'is-paypal-braintree-enabled',
    ApplePay: 'is-apple-pay-enabled-checkout',
    ChasePay: 'is-chasepay-enabled',
    VisaCheckout: 'is-visa-checkout-enabled',
    Klarna: 'is-klarna-payment-enabled',
    Paze: 'is-paze-payment-enabled',
};

const checkPaymentMethodDeviceSupport = (paymentMethodId, isGooglePaySupported) => {
    switch (paymentMethodId) {
        case 'ApplePay':
            return (typeof window !== 'undefined' && window.ApplePaySession);
        case 'Venmo': {
            const venmoSupported = isVenmoMethodSupported();
            return venmoSupported;
        }
        case 'GooglePay': {
            return isGooglePaySupported;
        }
        case 'Paze': {
            return (typeof window !== 'undefined' && window.DIGITAL_WALLET_SDK);
        }
        default:
            return true;
    }
};

const getEnabledPaymentMethods = (
    flags,
    allowedPaymentMethods,
    paymentMethodDetails,
    isGooglePaySupported,
) => {
    const enabledPaymentMethods = ['CreditCard'];

    allowedPaymentMethods.forEach((paymentMethodId) => {
        const paymentMethodFlag = listPaymentMethodFlags[paymentMethodId];
        if (flags[paymentMethodFlag]
            && paymentMethodDetails[paymentMethodId].active
            && checkPaymentMethodDeviceSupport(paymentMethodId, isGooglePaySupported)) {
            enabledPaymentMethods.push(paymentMethodId);
        }
    });

    return enabledPaymentMethods;
};

const getPaymentMethodsAllowedToPurchase = ({
    flags,
    amount,
    allPaymentMethod,
    orderContainsClubItemPayByCreditCard,
    hdFamilyBrandProductInCart,
    hasExternalCartProductInCart,
    passportSubscriptionStatus,
    orderContainsSubscriptionItem,
}) => {
    let paymentMethods = Object.keys(allPaymentMethod);
    const paymentMethodToPurchaseClubItems = ['CreditCard'];
    const paymentMethodToPurchaseSubscritionProducts = ['CreditCard'];
    const paymentMethodsDoNotSupportHDFamilyBrands = flags?.['which-payment-methods-do-not-support-hd-family-brands']?.notAllowedMethods || ['GooglePay'];
    const paymentMethodsExternalCartSupport = flags?.['which-payment-methods-allow-external-cart']?.allowedMethods || ['CreditCard'];
    const paymentMethodAllowedToPurchasePassport = flags?.['which-payment-methods-allow-passport']?.allowedMethods || ['CreditCard'];
    const klarnaAllowedAmount = flags?.['klarna-allowed-min-and-max-amount'] || {};
    const maxAmount = klarnaAllowedAmount.max || 1000;

    if (hasExternalCartProductInCart) {
        return paymentMethods.filter((payMethod) => paymentMethodsExternalCartSupport.includes(payMethod));
    }

    if (orderContainsClubItemPayByCreditCard) {
        return paymentMethodToPurchaseClubItems;
    }

    if (orderContainsSubscriptionItem?.length > 0) {
        if (flags['is-subscriptions-paypal-enabled']) {
            paymentMethodToPurchaseSubscritionProducts.push('PayPal');
        }
        if (flags['is-subscriptions-applepay-enabled']) {
            paymentMethodToPurchaseSubscritionProducts.push('ApplePay');
        }
        return paymentMethodToPurchaseSubscritionProducts;
    }

    if (passportSubscriptionStatus) {
        return paymentMethodAllowedToPurchasePassport;
    }

    if (hdFamilyBrandProductInCart) {
        paymentMethods = paymentMethods.filter((payMethod) => !paymentMethodsDoNotSupportHDFamilyBrands.includes(payMethod));
    }

    if (amount.orderTotal > maxAmount) {
        paymentMethods = paymentMethods.filter((payMethod) => payMethod !== 'Klarna');
    }

    return paymentMethods;
};

const EligiblePaymentMethods = React.memo(({
    allPaymentMethod,
    amount,
    appliedPromotions,
    callTrackEvent,
    callAttributionApplyPromoLinkedPaymentMethod,
    callRecordPaymentMethodType,
    children,
    flags,
    isBannerCodeTried,
    isGiftCardCoverFullOrderTotal,
    isGooglePayLoaded,
    isGooglePaySupported,
    isPaymentPageReady,
    hasExternalCartProductInCart,
    hdFamilyBrandProductInCart,
    orderContainsClubItemPayByCreditCard,
    orderContainsSubscriptionItem,
    passportSubscriptionStatus,
    selectedPaymentMethod,
    selectPaymentMethod,
}) => {
    const [paymentMethodReady, setPaymentMethodReady] = useState(false);
    const [paymentMethodsSupported, setPaymentMethodsSupported] = useState([]);

    const setEnabledPaymentMethodsAndReady = (enabledPaymentMethods) => {
        setPaymentMethodsSupported(enabledPaymentMethods);
        setPaymentMethodReady(true);
    };

    useEffect(
        () => {
            if (paymentMethodReady && isPaymentPageReady) {
                const cookies = new Cookies();
                const bannerCookie = cookies.get('banner');
                // bannerCookie?.mp - eg: PayPal
                const paymentMethodForPromotion = bannerCookie?.mp || '';
                const validPaymentMethodFromBannerCookie = !!allPaymentMethod[paymentMethodForPromotion];

                if (!isBannerCodeTried
                    && validPaymentMethodFromBannerCookie
                    && paymentMethodsSupported.includes(paymentMethodForPromotion)
                ) {
                    // Force select the payment of method of the promo if it is not
                    // selected
                    if (
                        paymentMethodForPromotion !== 'CreditCard'
                        && selectedPaymentMethod.id !== paymentMethodForPromotion
                    ) {
                        selectPaymentMethod({
                            id: allPaymentMethod[paymentMethodForPromotion].id,
                            name: allPaymentMethod[paymentMethodForPromotion].name,
                        });
                    }

                    if (isPaymentPageReady && appliedPromotions?.length <= 0) {
                        // apply promo in banner cookie and default to payment method in banner cookie
                        callAttributionApplyPromoLinkedPaymentMethod(paymentMethodsSupported);
                    }
                } else if (isGiftCardCoverFullOrderTotal) {
                    // Full GC Covers Order Total
                    callRecordPaymentMethodType();
                } else if (paymentMethodsSupported.includes(selectedPaymentMethod.id)) {
                    selectPaymentMethod({ id: selectedPaymentMethod.id, name: selectedPaymentMethod.name });
                    callRecordPaymentMethodType();
                } else if (!paymentMethodsSupported.includes('GiftCard')) {
                    selectPaymentMethod({ id: 'CreditCard', name: 'CreditCard' });
                    callRecordPaymentMethodType();
                }

                callTrackEvent({
                    eventCategory: 'Checkout',
                    eventAction: 'Payment Page - Payment Method Impression',
                    eventLabel: `${paymentMethodsSupported.join(',')}`,
                });
            }
        },
        [
            isPaymentPageReady,
            isBannerCodeTried,
            paymentMethodReady,
            isGiftCardCoverFullOrderTotal,
            paymentMethodsSupported.join(','),
        ],
    );

    useEffect(() => {
        if (isGooglePayLoaded !== null) {
            if (isGiftCardCoverFullOrderTotal && !hasExternalCartProductInCart) {
                setEnabledPaymentMethodsAndReady(['GiftCard']);
            } else {
                const allowedPaymentMethodForProduct = getPaymentMethodsAllowedToPurchase({
                    flags,
                    amount,
                    allPaymentMethod,
                    orderContainsClubItemPayByCreditCard,
                    hdFamilyBrandProductInCart,
                    hasExternalCartProductInCart,
                    passportSubscriptionStatus,
                    orderContainsSubscriptionItem,
                    isGiftCardCoverFullOrderTotal,
                });

                const enabledPaymentMethods = getEnabledPaymentMethods(
                    flags,
                    allowedPaymentMethodForProduct,
                    allPaymentMethod,
                    isGooglePaySupported,
                );

                setEnabledPaymentMethodsAndReady(enabledPaymentMethods);
            }
        }
    }, [
        isGooglePayLoaded,
        orderContainsClubItemPayByCreditCard,
        hdFamilyBrandProductInCart,
        passportSubscriptionStatus,
        orderContainsSubscriptionItem,
        isGiftCardCoverFullOrderTotal,
        hasExternalCartProductInCart,
    ]);

    const supportedPaymentMethods = React.useMemo(() => ({
        paymentMethodReady,
        paymentMethodsSupported,
    }));

    return (
        <PaymentMethodContext.Provider
            value={supportedPaymentMethods}
        >
            {children}
        </PaymentMethodContext.Provider>
    );
});

EligiblePaymentMethods.propTypes = {
    amount: object.isRequired,
    appliedPromotions: array.isRequired,
    flags: object.isRequired,
    isGooglePayLoaded: bool,
    isGooglePaySupported: bool,
    hdFamilyBrandProductInCart: bool.isRequired,
    hasExternalCartProductInCart: bool.isRequired,
    allPaymentMethod: object.isRequired,
    passportSubscriptionStatus: bool.isRequired,
    orderContainsSubscriptionItem: array.isRequired,
    orderContainsClubItemPayByCreditCard: bool.isRequired,
    children: element.isRequired,
    isGiftCardCoverFullOrderTotal: bool.isRequired,
    selectPaymentMethod: func.isRequired,
    callTrackEvent: func.isRequired,
    callAttributionApplyPromoLinkedPaymentMethod: func.isRequired,
    callRecordPaymentMethodType: func.isRequired,
    selectedPaymentMethod: object.isRequired,
    isPaymentPageReady: bool.isRequired,
    isBannerCodeTried: bool.isRequired,
};

EligiblePaymentMethods.defaultProps = {
    isGooglePayLoaded: null,
    isGooglePaySupported: null,
};

const mapStateToProps = (state) => ({
    appliedPromotions: getAppliedPromotions(state),
    flags: getFeatureFlags(state),
    amount: getAmount(state),
    selectedPaymentMethod: getPaymentMethod(state),
    allPaymentMethod: getPaymentMethods(state),
    isGiftCardCoverFullOrderTotal: getIsGiftCardCoverFullOrderTotal(state),
    passportSubscriptionStatus: getPassportSubscriptionStatus(state),
    hdFamilyBrandProductInCart: isHDFamilyBrandProductInCart(state),
    hasExternalCartProductInCart: isExternalCartProductInCart(state),
    orderContainsSubscriptionItem: getOrderContainsSubscriptionItem(state),
    orderContainsClubItemPayByCreditCard: getOrderContainsClubItemPayByCreditCard(state),
    isPaymentPageReady: getPaymentPageReady(state),
    isBannerCodeTried: getIsBannerCodeTried(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    selectPaymentMethod: setPaymentMethod,
    callTrackEvent: trackEvent,
    callAttributionApplyPromoLinkedPaymentMethod: attributionApplyPromoLinkedPaymentMethod,
    callRecordPaymentMethodType: recordPaymentMethodType,
}, dispatch);

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
)(EligiblePaymentMethods);
